

import Parser from 'html-react-parser'
import _ from '@/utils/lodash-optimized'
import moment from 'moment'
import React from 'react'
import { MdWarningAmber } from 'react-icons/md'
import { getIssueExtension, IssueExtensions } from 'shared_src/mappings/issue'

import ActionStatus from '../../../../lib/Codes/ActionStatuses'
import {
  narusBlue,
  narusGreen,
  narusRed
} from '../../../shared/NarusMaterial/colors'

import DueDays from './DueDays'

const domains = {
  DTP: <div className='igao-icon circle teal'> Tx </div>,
  DRS: <div className='igao-icon circle dark-red'> Ox </div>,
  DPE: <div className='igao-icon square light-blue'> Px </div>,
  DPMH: <div className='igao-icon square light-grey'> Hx </div>,
  DPS: <div className='igao-icon circle dark-red'> Sx </div>,
  DMA: <div className='igao-icon square red'> Rx </div>,
  DHEDV: <div className='igao-icon square green'> Ex </div>,
  DPAE: <div className='igao-icon circle light-purple'> Ax </div>,
  DPFC: <div className='igao-icon circle dark-orange'> Fx </div>,
  DKC: <div className='igao-icon circle grey'> Kx </div>,
  DGEP: <div className='igao-icon circle purple'> Gx </div>,
  DDEM: <div className='igao-icon square black'> Nx </div>,
  DBDC: <div className='igao-icon circle blue'> Bx </div>,
  DV: <div className='igao-icon square orange'> Vx </div>
}

const calculateEndDate = actions => {
    const findDate = action => action?.scheduledPeriod?.end

  return actions.map(a => findDate(a.detail)).sort()[0]
}

const addTrendSort = trend =>
  ({
    'ahead-of-target': 1,
    'on-target': 2,
    'behind-target': 3
  }[trend] || 4)

const addWeightSort = (weight, category) =>
  (category === 'Clinical Insights' ? weight + 10 : weight)

const addPrioritySort = (priority, weight) =>
  ({
    high: 1 + weight,
    medium: 10 + weight,
    low: 20 + weight
  }[priority] || 40)

const byTypeText = txt => id => id && id.type && id.type.text === txt

const findTrend = trend => {
  const trends = {
    'ahead-of-target': {
      label: 'Improving',
      icon: 'fa fa-arrow-up',
      style: { color: narusGreen },
      value: 'ahead-of-target'
    },
    'on-target': {
      label: 'Unchanged',
      icon: 'fa fa-arrow-right',
      style: { color: narusBlue },
      value: 'on-target'
    },
    'behind-target': {
      label: 'Worsening',
      icon: 'fa fa-arrow-down',
      style: { color: narusRed },
      value: 'behind-target'
    },
    'error': {
      label: '',
      icon: '',
      style: {},
      value: ''
    }
  }
  return trends[trend] || trends.error
}

const igaoHeader = [
  {
    displayFn: r =>
      domains[
          ((r?.issue?.identifier ?? []).find(byTypeText('DomainID')) || {})
          .value
      ],
    title: 'Domain',
    sortFn: (a, b) => {
        const weightA = getIssueExtension(a, IssueExtensions.weight)?.valueDecimal ?? 0
        const weightB = getIssueExtension(b, IssueExtensions.weight)?.valueDecimal ?? 0
      const categoryA = a.issue.identifier.find(i => {
        if (i.type.text === 'DomainType') {
          return i
        }
      }).value
      const categoryB = b.issue.identifier.find(i => {
        if (i.type.text === 'DomainType') {
          return i
        }
      }).value
      const weightSortA = addWeightSort(weightA, categoryA)
      const weightSortB = addWeightSort(weightB, categoryB)

      if (weightSortA > weightSortB) {
        return 1
      }
      if (weightSortA === weightSortB) {
        return 0
      }
      return -1
    }
  },
  {
    displayFn: r => {
        if (r?.esasDescription ?? false) {
        return r.esasDescription()
      }
      return Parser(r?.issue?.description || '', { trim: true })
    },
    title: 'Issue',
    sortFn: (a, b) => {
        const aDescription = a?.issue?.description
        const bDescription = b?.issue?.description
      if (aDescription > bDescription) {
        return 1
      }
      if (aDescription === bDescription) {
        return 0
      }
      return -1
    }
  },
  {
      displayFn: r => r?.issue?.author?.[0]?.display ?? 'Unassigned',
    title: 'Assigned'
  },
  {
    displayFn: r => {
        const status = r?.issue?.status
      const s = ActionStatus.mapAll.find(item => status === item.value)
      return s ? s.label : null
    },
    title: 'Status'
  },
  {
    displayFn: r => {
      const createdAt = getIssueExtension(
        r,
        'http://narushealth.com/fhir/extensions/CarePlan/created_at'
      )
      if (!createdAt || !createdAt.valueInstant) {
        return null
      }
      const diff = moment().diff(createdAt.valueInstant, 'days')
      if (diff === 0) {
        return 'Today'
      }
      if (diff > 1) {
        return `${diff} days`
      }
      return `${diff} day`
    },
    title: 'Days Open'
  },
  {
      displayFn: r => _.capitalize(r?.goal?.priority?.coding?.[0]?.code),
    title: 'Priority',
    sortFn: (a, b) => {
        const weightA = getIssueExtension(a, IssueExtensions.weight)?.valueDecimal ?? 0
        const weightB = getIssueExtension(b, IssueExtensions.weight)?.valueDecimal ?? 0

        const priorityA = a?.goal?.priority?.coding?.[0]?.code
        const priorityB = b?.goal?.priority?.coding?.[0]?.code

      const prioritySortA = addPrioritySort(priorityA, weightA)
      const prioritySortB = addPrioritySort(priorityB, weightB)

      if (prioritySortA > prioritySortB) {
        return 1
      }
      if (prioritySortA === prioritySortB) {
        return 0
      }
      return -1
    }
  },
  {
    displayFn: r => {
        const trend = r?.goal?.status
      const t = findTrend(trend)
      return t
        ? (
          <i
            className={t.icon}
            style={t.style}
          />
        )
        : null
    },
    title: 'Trend',
    sortFn: (a, b) => {
        const trendA = addTrendSort(a?.goal?.status ?? '')
        const trendB = addTrendSort(b?.goal?.status ?? '')

      if (trendA > trendB) {
        return 1
      }
      if (trendA === trendB) {
        return 0
      }
      return -1
    }
  },
  {
    displayFn: r => {
      const actionsLength = (r?.issue?.activity ?? []).filter(a => {
        if (
          a.detail.status === 'not-started' ||
          a.detail.status === 'in-progress'
        ) {
          return a
        }
      }).length

      return actionsLength === 0 ? '' : actionsLength
    },
    sortFn: (a, b) => {
      const lengthA = (a?.issue?.activity ?? []).filter(i => {
        if (
          i.detail.status === 'not-started' ||
          i.detail.status === 'in-progress'
        ) {
          return i
        }
      }).length

      const lengthB = (b?.issue?.activity ?? []).filter(i => {
        if (
          i.detail.status === 'not-started' ||
          i.detail.status === 'in-progress'
        ) {
          return i
        }
      }).length

      if (lengthA > lengthB) {
        return 1
      }
      if (lengthA === lengthB) {
        return 0
      }
      return -1
    },
    title: 'Actions'
  },
  {
    displayFn: r => {
        const status = r?.issue?.status
      if (
        status === 'completed' ||
        status === 'cancelled' ||
        status === 'deferred'
      ) {
        return ''
      }
      if (
          (r?.issue?.activity ?? []).filter(
          a =>
            ![ 'completed', 'cancelled', 'deferred', 'on-hold' ].includes(
                a?.detail?.status
            )
        ).length
      ) {
        const actions = calculateEndDate(
            r?.issue?.activity.filter(
            a =>
              ![ 'completed', 'cancelled', 'deferred', 'on-hold' ].includes(
                  a?.detail?.status
              )
          )
        )
        return (
          <div style={{ display: 'flex' }}>
            <DueDays date={actions} />
            {r.urgent
              ? (
                <div style={{ paddingLeft: '8px' }}>
                  <MdWarningAmber color='#F2545A' />
                </div>
              )
              : (
                ''
              )}
          </div>
        )
      }
    },
    title: 'Next Action',
    sortFn: (a, b) => {
      if (a.urgent && !b.urgent) {
        return -1
      }
      if (b.urgent && !a.urgent) {
        return 1
      }
      const endDateA = calculateEndDate(
          a?.issue?.activity.filter(
          action =>
            ![ 'completed', 'cancelled', 'deferred', 'on-hold' ].includes(
                action?.detail?.status
            )
        )
      )
      const endDateB = calculateEndDate(
          b?.issue?.activity.filter(
          action =>
            ![ 'completed', 'cancelled', 'deferred', 'on-hold' ].includes(
                action?.detail?.status
            )
        )
      )
      if (endDateA && !endDateB) {
        return -1
      }
      if (!endDateA && endDateB) {
        return 1
      }
      if (endDateA < endDateB) {
        return -1
      }
      if (endDateA > endDateB) {
        return 1
      }
      const isOpenA = ![
        'completed',
        'cancelled',
        'deferred',
        'on-hold'
      ].includes(a?.issue?.status)
      const isOpenB = ![
        'completed',
        'cancelled',
        'deferred',
        'on-hold'
      ].includes(b?.issue?.status)
      if (isOpenA && !isOpenB) {
        return -1
      }
      if (!isOpenA && isOpenB) {
        return 1
      }
      return 0
    }
  }
]

const patientFolderHeaders = [
  'Issue',
  'Assigned',
  'Priority',
  'Trend',
  'Status',
  'Actions',
  'Next Action'
]

const patientHeader = []
patientFolderHeaders.forEach(i => {
  const item = igaoHeader.find(h => h.title === i)
  if (item) {
    patientHeader.push(item)
  }
})
export { igaoHeader, patientHeader }
